import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Heart, Sparkles, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ProductCard from '../components/Products/ProductCard';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories] = useState([
    { id: 'brumes', name: 'Brumes', image: 'https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/9uxm2dqt_IMG_1092.jpeg' },
    { id: 'cremes', name: 'Crèmes', image: 'https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/r4ow0ymx_IMG_1089.jpeg' },
    { id: 'musc', name: 'Musc', image: 'https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/2lrxsx06_IMG_1091.jpeg' },
    { id: 'barbes', name: 'Barbes', image: 'https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/nxbtfjt0_IMG_1086.jpeg' },
  ]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden" data-testid="hero-section">
        <div className="container-custom grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-0 min-h-[90vh]">
          {/* Hero Text */}
          <div className="animate-fade-in-up z-10">
            <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
              Cosmétiques de luxe
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-text-primary leading-tight mb-6">
              L'instant{' '}
              <span className="italic text-accent">douceur</span>
            </h1>
            <p className="text-text-secondary text-base lg:text-lg max-w-md mb-8 leading-relaxed">
              Découvrez notre collection exclusive de parfums intimes et soins corporels. 
              Des formules d'exception pour sublimer votre peau et éveiller vos sens.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button className="btn-primary flex items-center gap-2" data-testid="hero-shop-btn">
                  Découvrir
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/#about">
                <Button variant="outline" className="btn-outline" data-testid="hero-story-btn">
                  Notre Histoire
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative lg:h-[90vh] flex items-center justify-center">
            <div className="relative w-full max-w-lg">
              <div className="absolute inset-0 bg-rose-100/50 rounded-full blur-3xl transform scale-110"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/t19sy0d3_IMG_1085.jpeg"
                alt="Secret Musc Collection"
                className="relative z-10 w-full h-auto object-cover rounded shadow-rose-lg animate-fade-in-up"
                style={{ animationDelay: '0.2s' }}
              />
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-rose-100 rounded-full blur-2xl opacity-60"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-rose-200 rounded-full blur-3xl opacity-40"></div>
      </section>

      {/* Categories Section */}
      <section className="section-pink py-24" data-testid="categories-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
              Nos Collections
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-text-primary">
              Explorez nos univers
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="group relative overflow-hidden aspect-[3/4] bg-white"
                data-testid={`category-${category.id}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-serif text-xl text-white group-hover:text-rose-100 transition-colors">
                    {category.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-cream py-24" data-testid="featured-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-4">
            <div>
              <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
                Sélection
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-text-primary">
                Nos coups de cœur
              </h2>
            </div>
            <Link 
              to="/products" 
              className="text-accent hover:text-accent-hover transition-colors flex items-center gap-2 text-sm uppercase tracking-widest font-medium"
              data-testid="view-all-products"
            >
              Voir tout
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="product-grid">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-pink py-24" data-testid="about-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-rose-100 rounded"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/nxbtfjt0_IMG_1086.jpeg"
                alt="Rituel Musc - Collection Secret Musc"
                className="relative z-10 w-full h-auto object-cover rounded shadow-rose-lg"
              />
            </div>

            {/* Content */}
            <div>
              <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
                Notre Histoire
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-text-primary mb-6">
                Une vision,<br />
                <span className="italic">une passion</span>
              </h2>
              <p className="text-text-secondary leading-relaxed mb-6">
                Secret Musc est né de la vision audacieuse d'<strong>Ikram</strong>, une femme qui a cru 
                en l'authenticité et la confiance en soi. Avec détermination et passion, elle a créé 
                une marque unique de brumes corporelles musquées, de soins pour la barbe et la peau, 
                pensés pour sublimer chaque personne au quotidien.
              </p>
              <p className="text-text-secondary leading-relaxed mb-6">
                Chaque produit Secret Musc reflète son engagement profond à allier élégance, qualité 
                et caractère unique. Des fragrances soigneusement sélectionnées, des formules douces 
                pour la peau, et un packaging raffiné qui fait de chaque utilisation un moment de 
                bien-être précieux.
              </p>
              <p className="text-text-secondary leading-relaxed mb-8">
                Secret Musc, c'est bien plus qu'une marque de cosmétiques — c'est l'expression d'un 
                rêve devenu réalité, porté par une femme inspirante qui souhaite que chacun se sente 
                unique, confiant et sublimé. Parce que vous méritez ce qu'il y a de meilleur.
              </p>

              {/* Values */}
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-white shadow-rose flex items-center justify-center mx-auto mb-3">
                    <Heart className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm text-text-secondary">Créé avec amour</span>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-white shadow-rose flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm text-text-secondary">Qualité premium</span>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-white shadow-rose flex items-center justify-center mx-auto mb-3">
                    <Star className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm text-text-secondary">Authenticité</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-cream py-24" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
              Témoignages
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-text-primary">
              Ce qu'elles en disent
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Marie L.',
                text: 'Les brumes Secret Musc sont devenues indispensables dans ma routine. Le parfum est subtil et tient toute la journée.',
                rating: 5
              },
              {
                name: 'Sophie D.',
                text: 'J\'ai offert le coffret à ma mère, elle a adoré ! Les packagings sont magnifiques et les senteurs divines.',
                rating: 5
              },
              {
                name: 'Léa M.',
                text: 'La crème Vanille Coco est un vrai délice. Ma peau est douce et délicatement parfumée. Je recommande à 100% !',
                rating: 5
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white p-8 shadow-rose"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-text-secondary italic mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <p className="font-serif text-text-primary">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-pink py-24" data-testid="contact-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
              Contact
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-text-primary mb-6">
              Une question ?
            </h2>
            <p className="text-text-secondary mb-12">
              Notre équipe est à votre écoute pour répondre à toutes vos questions.
            </p>

            {contactSuccess ? (
              <div className="bg-white p-8 shadow-rose rounded animate-fade-in-up" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-success" />
                </div>
                <h3 className="font-serif text-2xl text-text-primary mb-4">
                  Message envoyé ! ✨
                </h3>
                <p className="text-text-secondary mb-6">
                  Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.
                </p>
                <Button
                  onClick={() => setContactSuccess(false)}
                  className="btn-outline"
                >
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form 
                onSubmit={handleContactSubmit}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Votre nom"
                    className="input-elegant"
                    data-testid="contact-name"
                    required
                  />
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="Votre email"
                    className="input-elegant"
                    data-testid="contact-email"
                    required
                  />
                </div>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="Votre message"
                  rows={5}
                  className="input-elegant w-full resize-none"
                  data-testid="contact-message"
                  required
                ></textarea>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center gap-2 mx-auto"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? 'Envoi en cours...' : (
                    <>
                      Envoyer
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
